import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fireworkTrackAddToCart } from '@/utils/firework/firework-analytics';

import { QUERY_KEYS } from 'Api/constants';
import client from 'Api/datasources/bff-datasources';
import type { AnalyticsEventName } from '@iceberg/report-perfect-experience-analytics';
import { ANALYTICS_EVENT_NAMES, collectMetrics } from '@iceberg/report-perfect-experience-analytics';
import { ANALYTICS_API_NAMES, UNKNOWN } from '@/data/constants';
import { isSuccessResponse } from '@/utils';
import { getExistingCartGuid, getLoggedInStatus, isUserAuthenticated } from 'Utils/auth';

const userStatus = () => getLoggedInStatus().toLowerCase();
const cartUrlParam = () => (isUserAuthenticated() ? userStatus() : getExistingCartGuid());
const cartUrl = () => `/users/${userStatus()}/carts/${cartUrlParam()}`;

export const addToCartReducer = (data: any, guid = '') => {
  const { entry, subtotal } = data;
  return {
    success: true,
    message: `Cart item ${entry?.product?.code} has been added`,
    cart: {
      id: guid,
      subTotal: {
        currencyIso: subtotal?.currencyIso,
        formattedValue: subtotal?.formattedValue,
        value: parseFloat(subtotal?.value),
      },
      entryNumber: entry?.entryNumber,
    },
  };
};

export const getCart = async () => {
  const params = { loyaltyInfo: 'simple' };
  const trackPageRenderMetrics = (eventName: AnalyticsEventName) => {
    collectMetrics(eventName, {
      detail: ANALYTICS_API_NAMES.CART_BY_ID(userStatus(), cartUrlParam()),
    });
  };
  trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_INIT);
  const response = await client.get(`${cartUrl()}`, { params });
  const isSuccess = isSuccessResponse(response);

  if (isSuccess) {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_READY);
  } else {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_ERROR);
  }
  return response?.data;
};

export const createCart = async () => {
  const userStatusVal = userStatus();
  const trackPageRenderMetrics = (eventName: AnalyticsEventName) => {
    collectMetrics(eventName, {
      detail: ANALYTICS_API_NAMES.CREATE_CART(userStatusVal),
    });
  };
  trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_INIT);
  const response = await client.post(`/users/${userStatusVal}/carts`, {});
  const isSuccess = isSuccessResponse(response);

  if (isSuccess) {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_READY);
  } else {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_ERROR);
  }
  return response?.data;
};

export const addToCart = async (productId: any, quantity: any, guid = getExistingCartGuid()) => {
  const userStatusVal = userStatus();
  const guidParam = isUserAuthenticated() ? userStatus() : guid;
  const params = { code: productId, qty: quantity };
  const trackPageRenderMetrics = (eventName: AnalyticsEventName) => {
    collectMetrics(eventName, {
      detail: ANALYTICS_API_NAMES.CART_SYNC(guidParam, userStatusVal),
    });
  };
  trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_INIT);
  const response = await client.post(`/users/${userStatusVal}/carts/${guidParam}/entries`, {}, { params });
  const isSuccess = isSuccessResponse(response);

  if (isSuccess) {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_READY);
  } else {
    trackPageRenderMetrics(ANALYTICS_EVENT_NAMES.API_ERROR);
  }
  const { entry } = response?.data || {};
  const { product } = entry || {};

  if (product) {
    fireworkTrackAddToCart(product, entry.quantity);
  }

  return addToCartReducer(response?.data, guid);
};

export const useAddItemToCart = (mutationOptions = {}) => {
  const {
    mutate: addItemToCart,
    isLoading: loading,
    error,
    data,
  } = useMutation({
    mutationFn: ({ productId, quantity, guid }: { productId: any; quantity: any; guid?: any }) =>
      addToCart(productId, quantity, guid),
    ...mutationOptions,
  });
  return { addItemToCart, loading, error, data };
};

export const useCreateAndAddItemToCart = (mutationOptions = {}) => {
  const {
    addItemToCart,
    loading: addItemToCartLoading,
    error: addItemToCartError,
    data: addItemToCartData,
  } = useAddItemToCart(mutationOptions);
  const {
    mutate: createAndAddToCart,
    isLoading: loading,
    error,
  } = useMutation({
    mutationFn: () => createCart(),
    onSuccess: (data, payload: any) => {
      addItemToCart({ ...payload, guid: data?.guid });
    },
  });
  return {
    createAndAddToCart,
    addItemToCart,
    loading: loading || addItemToCartLoading,
    error: error || addItemToCartError,
    data: addItemToCartData,
  };
};

export const useAddToCart = (mutationOptions = {}) => {
  const { createAndAddToCart, addItemToCart, loading, error, data } = useCreateAndAddItemToCart(mutationOptions);
  const queryClient = useQueryClient();
  const addToCart = async ({ productId, quantity }: { productId: any; quantity: any }) => {
    const guestGuid = getExistingCartGuid();
    if (isUserAuthenticated()) {
      try {
        await queryClient.fetchQuery({
          queryKey: [QUERY_KEYS.CART],
          queryFn: () => getCart(),
          staleTime: 10 * 60 * 1000,
        });
        addItemToCart({ productId, quantity });
      } catch (error) {
        createAndAddToCart({ productId, quantity });
      }
    } else {
      if (guestGuid) {
        addItemToCart({ productId, quantity });
      } else {
        createAndAddToCart({ productId, quantity });
      }
    }
  };
  return {
    addToCart,
    loading,
    error,
    data,
  };
};
